import { buildSectionPathLabel } from '@/features/idea-lens/ideaLayout';
import type { Project, ProjectResumeCheckpoint, ResumeEditorAnchor, ResumeWorkspaceKind, Section } from '@/types';
import { buildTextAnchorContext } from '@/shared/utils/textAnchor';

export const PROJECT_RESUME_CHECKPOINT_KEY = 'wrlensResumeCheckpoint.v1';
export const PROJECT_RESUME_SCHEMA_VERSION = 1;

function asObject(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function clampNumber(value: unknown): number | null {
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function sanitizeText(value: unknown, maxLength: number): string {
  if (typeof value !== 'string') return '';
  return value.trim().slice(0, maxLength);
}

function sanitizeWorkspace(value: unknown): ResumeWorkspaceKind | null {
  return value === 'editor' || value === 'global-lens' || value === 'idea-lens' ? value : null;
}

function sanitizeDeviceClass(value: unknown): 'mobile' | 'desktop' | null {
  return value === 'mobile' || value === 'desktop' ? value : null;
}

export function sanitizeResumeEditorAnchor(input: unknown): ResumeEditorAnchor | undefined {
  const record = asObject(input);
  if (!record) return undefined;
  const scrollTop = clampNumber(record.scrollTop);
  const selectionStart = clampNumber(record.selectionStart);
  const selectionEnd = clampNumber(record.selectionEnd);
  if (scrollTop == null || selectionStart == null || selectionEnd == null) return undefined;
  return {
    scrollTop: Math.max(0, scrollTop),
    selectionStart: Math.max(0, selectionStart),
    selectionEnd: Math.max(Math.max(0, selectionStart), selectionEnd),
    anchorText: sanitizeText(record.anchorText, 240) || undefined,
    prefixText: sanitizeText(record.prefixText, 240) || undefined,
    suffixText: sanitizeText(record.suffixText, 240) || undefined,
  };
}

export function sanitizeProjectResumeCheckpoint(input: unknown): ProjectResumeCheckpoint | null {
  const record = asObject(input);
  if (!record) return null;
  const schemaVersion = clampNumber(record.schemaVersion);
  const workspace = sanitizeWorkspace(record.workspace);
  const deviceClass = sanitizeDeviceClass(record.deviceClass);
  const updatedAt = typeof record.updatedAt === 'string' ? record.updatedAt : '';
  const deviceId = typeof record.deviceId === 'string' ? record.deviceId : '';
  const versionId = typeof record.versionId === 'string' ? record.versionId : '';
  const sectionId = typeof record.sectionId === 'string' ? record.sectionId : '';
  const previewTitle = sanitizeText(record.previewTitle, 160);
  const previewPath = sanitizeText(record.previewPath, 320);
  const previewSnippet = sanitizeText(record.previewSnippet, 320);
  if (
    schemaVersion == null
    || !workspace
    || !deviceClass
    || !updatedAt
    || !deviceId
    || !versionId
    || !sectionId
    || !previewTitle
    || !previewPath
  ) {
    return null;
  }
  return {
    schemaVersion,
    updatedAt,
    deviceId,
    deviceClass,
    workspace,
    versionId,
    sectionId,
    ideaNodeId: typeof record.ideaNodeId === 'string' && record.ideaNodeId ? record.ideaNodeId : undefined,
    globalLensSectionId: typeof record.globalLensSectionId === 'string' && record.globalLensSectionId ? record.globalLensSectionId : undefined,
    previewTitle,
    previewPath,
    previewSnippet,
    editorSnapshot: sanitizeResumeEditorAnchor(record.editorSnapshot),
  };
}

export function readProjectResumeCheckpoint(project?: Pick<Project, 'metadata'> | null): ProjectResumeCheckpoint | null {
  const customFields = asObject(project?.metadata?.customFields);
  return sanitizeProjectResumeCheckpoint(customFields?.[PROJECT_RESUME_CHECKPOINT_KEY]);
}

export function writeProjectResumeCheckpoint(
  customFields: Record<string, unknown> | undefined,
  checkpoint: ProjectResumeCheckpoint,
): Record<string, unknown> {
  return {
    ...(customFields ?? {}),
    [PROJECT_RESUME_CHECKPOINT_KEY]: checkpoint,
  };
}

export function removeProjectResumeCheckpoint(customFields: Record<string, unknown> | undefined): Record<string, unknown> {
  if (!customFields || !(PROJECT_RESUME_CHECKPOINT_KEY in customFields)) return customFields ?? {};
  const next = { ...customFields };
  delete next[PROJECT_RESUME_CHECKPOINT_KEY];
  return next;
}

export function pickNewerResumeCheckpoint(
  left: ProjectResumeCheckpoint | null,
  right: ProjectResumeCheckpoint | null,
): ProjectResumeCheckpoint | null {
  if (!left) return right;
  if (!right) return left;
  const leftTime = Date.parse(left.updatedAt);
  const rightTime = Date.parse(right.updatedAt);
  if (!Number.isFinite(leftTime)) return right;
  if (!Number.isFinite(rightTime)) return left;
  return rightTime >= leftTime ? right : left;
}

export function mergeCustomFieldsWithResumeCheckpoint(
  baseCustomFields: Record<string, unknown> | undefined,
  incomingCustomFields: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const merged = {
    ...(baseCustomFields ?? {}),
    ...(incomingCustomFields ?? {}),
  };
  const checkpoint = pickNewerResumeCheckpoint(
    sanitizeProjectResumeCheckpoint(baseCustomFields?.[PROJECT_RESUME_CHECKPOINT_KEY]),
    sanitizeProjectResumeCheckpoint(incomingCustomFields?.[PROJECT_RESUME_CHECKPOINT_KEY]),
  );
  if (checkpoint) {
    merged[PROJECT_RESUME_CHECKPOINT_KEY] = checkpoint;
  } else {
    delete merged[PROJECT_RESUME_CHECKPOINT_KEY];
  }
  return merged;
}

export function buildResumeEditorAnchor(
  content: string,
  selectionStart: number,
  selectionEnd: number,
): ResumeEditorAnchor {
  const safeStart = Math.max(0, Math.min(selectionStart, content.length));
  const safeEnd = Math.max(safeStart, Math.min(selectionEnd, content.length));
  const isCollapsed = safeStart === safeEnd;
  const anchorStart = isCollapsed ? Math.max(0, safeStart - 48) : safeStart;
  const anchorEnd = isCollapsed ? Math.min(content.length, safeEnd + 48) : safeEnd;
  const context = buildTextAnchorContext(content, anchorStart, anchorEnd);
  return {
    scrollTop: 0,
    selectionStart: safeStart,
    selectionEnd: safeEnd,
    anchorText: context?.text,
    prefixText: context?.prefixText,
    suffixText: context?.suffixText,
  };
}

export function buildResumeSectionPath(sectionId: string, sections: Section[]): string {
  const sectionById = new Map(sections.map((section) => [section.id, section] as const));
  return buildSectionPathLabel(sectionId, sectionById);
}

export function buildResumePreviewSnippet(content: string, selectionStart: number, selectionEnd: number): string {
  const source = content.replace(/\s+/g, ' ').trim();
  if (!source) return '';
  const safeStart = Math.max(0, Math.min(selectionStart, content.length));
  const safeEnd = Math.max(safeStart, Math.min(selectionEnd, content.length));
  const highlight = content.slice(safeStart, safeEnd).replace(/\s+/g, ' ').trim();
  if (highlight) {
    return highlight.slice(0, 180);
  }
  const collapsedSource = content.replace(/\s+/g, ' ');
  const contextStart = Math.max(0, safeStart - 72);
  const contextEnd = Math.min(collapsedSource.length, safeStart + 108);
  return collapsedSource.slice(contextStart, contextEnd).trim().slice(0, 180);
}

export function describeResumeWorkspace(workspace: ResumeWorkspaceKind): 'projects.resume_workspace_editor' | 'projects.resume_workspace_global_lens' | 'projects.resume_workspace_idea_lens' {
  switch (workspace) {
    case 'global-lens':
      return 'projects.resume_workspace_global_lens';
    case 'idea-lens':
      return 'projects.resume_workspace_idea_lens';
    case 'editor':
    default:
      return 'projects.resume_workspace_editor';
  }
}
